import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { PartnerInterfaceGlobal } from '../../models';
import { AuthService, FillStateService, LaravelEchoService, AmplitudeService } from '../../services';
import { UserRole } from '../../enum';
import { SelectItemsInterface } from '../../models';
import { HelpersInterface } from '../../interfaces';

interface MenuItem {
  icon: string;
  label: string;
  children?: MenuItem[];
  isOpen?: boolean;
  isLast?: boolean;
  isFirst?: boolean;
  routeUr?: string;
  isShow?: boolean;
  tooltip: string;
  isDisabled?: boolean;
  isNew?: boolean;
}

@Injectable()
export class AppConfig {
  public partnerInfo$ = new BehaviorSubject<PartnerInterfaceGlobal.IPartnerInfo | null>(null);
  public contentHeight$ = new BehaviorSubject<string>('1000px');

  public isCollapsed$ = new BehaviorSubject<boolean>(false);

  public menuItems: MenuItem[] = [
    {
      icon: 'ng-zorro:platformSettings',
      label: 'General Settings',
      isShow: true,
      routeUr: '/settings',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:frameworks',
      label: 'Policies & Frameworks',
      isShow: true,
      isDisabled: false,
      routeUr: '/frameworks',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:integrations',
      label: 'Integrations',
      isShow: true,
      isDisabled: false,
      routeUr: '/integrations',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:trafficAnalyzer',
      label: 'Traffic Analyzer',
      isShow: true,
      isDisabled: false,
      isOpen: false,
      tooltip: '',
      children: [
        { icon: '', label: 'Dashboard', routeUr: '/traffic-analyzer/dashboard', tooltip: '' },
        { icon: '', label: 'Rule Manager', routeUr: '/traffic-analyzer/rule-manager', tooltip: '' },
      ]
    },
    {
      icon: 'ng-zorro:scanners',
      label: 'Scanners',
      isShow: true,
      isDisabled: false,
      routeUr: '/scanners',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:companies',
      label: 'Companies',
      isShow: true,
      isDisabled: false,
      routeUr: '/companies',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:mismatches',
      label: 'Mismatches',
      isShow: true,
      isLast: true,
      isDisabled: false,
      routeUr: '/mismatches',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:account',
      label: 'User Management',
      isShow: true,
      isDisabled: false,
      routeUr: '/users',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:payments',
      label: 'Payments',
      isShow: true,
      isDisabled: false,
      isOpen: false,
      isNew: true,
      tooltip: '',
      children: [
        { icon: '', label: 'Invoices', routeUr: '/invoices', tooltip: '' },
        { icon: '', label: 'Payment Information', routeUr: '/payment-information', tooltip: '' },
      ]
    },
    {
      icon: 'ng-zorro:contactUsSidebar',
      label: 'Contact Us',
      isShow: true,
      isDisabled: false,
      routeUr: '',
      tooltip: '',
    },
    {
      icon: 'ng-zorro:knowledgeBase',
      label: 'Knowledge Base',
      isShow: true,
      isDisabled: false,
      tooltip: 'Access the Knowledge Base for detailed guides and FAQs',
    },
    {
      icon: 'ng-zorro:notifications',
      label: 'Alerts',
      isShow: true,
      isDisabled: false,
      tooltip: '',
    },
    {
      icon: 'ng-zorro:logOut',
      label: 'Logout',
      isShow: true,
      isDisabled: false,
      tooltip: '',
    },
  ];

  defaultNodeOrder: HelpersInterface.SelectItemHelper<number, string>[] = [{ id: -1, name: 'Default order' }];

  public menuItems$ = new BehaviorSubject<MenuItem[]>(this.menuItems);

  public schainValues$ = new BehaviorSubject<SelectItemsInterface.ISchainValue[]>([]);

  public schainNodeOrderItems$ = new BehaviorSubject<SelectItemsInterface.ISchainNodeOrder[]>([]);

  constructor(
    private authService: AuthService,
    private fillStateService: FillStateService,
    private laravelEchoService: LaravelEchoService,
    private amplitudeService: AmplitudeService,
  ) { }

  async init(): Promise<void> {
    return new Promise<void>(async (resolve) => {
      if (this.authService.isAuthenticated()) {
        const user = JSON.parse(localStorage.getItem('user-info') || '{}');
        if (user) {
          this.amplitudeService.initAmplitude(user.email);
        }

        if (this.authService.userRole !== UserRole.external) {
          this.fillStateService.handle();
          try {
            const res = await this.authService.getAllPartnerInfo().toPromise();
            this.partnerInfo$.next(res.data);
            this.authService.partner = res.data;

            // init Echo service for socket connections
            this.laravelEchoService.initEcho(this.authService.token);

            if (res.data.id === 300 && this.authService.userRole === UserRole.support) {
              const newItem = {
                icon: 'ng-zorro:admin',
                label: 'Admin Settings',
                isShow: true,
                isLast: true,
                isDisabled: false,
                tooltip: '',
                children: [
                  { icon: '', label: 'General', routeUr: '/general', tooltip: '' },
                  { icon: '', label: 'Modules', routeUr: '/modules', tooltip: '' },
                ]
              };
              const mismatchIndex = this.menuItems.findIndex((item) => item.label === 'Mismatches');
              this.menuItems.splice(mismatchIndex + 1, 0, newItem);
            }

            const trafficAnalyzerItem = this.menuItems.find((item) => item.label === 'Traffic Analyzer');

            if (this.partnerInfo$.value?.partner_plan !== 1) {
              if (trafficAnalyzerItem) {
                trafficAnalyzerItem.tooltip = `Traffic Analyzer is available on Enterprise version.
                If you want to upgrade your platform, please contact our Business Development Manager`;
              }
            }

            if (this.partnerInfo$.value?.partner_plan === 1 && !this.partnerInfo$.value?.is_traffic_analyzer) {
              if (trafficAnalyzerItem) {
                trafficAnalyzerItem.tooltip = 'To activate Traffic Analyzer please contact your dedicated Account Manager';
              }
            }

            this.menuItems$.next(this.menuItems);

          } catch (error) { }
        } else {
          this.menuItems = this.menuItems.filter((item) => item.label === 'Logout');

          this.menuItems$.next(this.menuItems);
        }
      }

      setTimeout(() => resolve(), 100);
    });
  }

  public updateSchainNodeOrderItems(companyId: number, schainValues: SelectItemsInterface.ISchainValue[], callback: () => void): void {
    const defaultItems: SelectItemsInterface.ISchainNodeOrder[] = [
      { id: 1, name: 'Default order', isPresentSellerId: true, parentId: 0 }
    ];

    const customNode = schainValues.find(item => item.id === companyId);

    if (!customNode || companyId === 1) {
      this.schainNodeOrderItems$.next(defaultItems);
      return;
    }

    const schainNodeOrderItems = [
      ...defaultItems,
      {
        id: 2, name: `Default + ${customNode.sellerName}`,
        isPresentSellerId: customNode.defaultCustomSellerId ? true : false,
        parentId: customNode.id
      },
      {
        id: 3, name: `${customNode.sellerName} + Default`,
        isPresentSellerId: customNode.customDefaultSellerId ? true : false,
        parentId: customNode.id
      },
    ];

    this.schainNodeOrderItems$.next(schainNodeOrderItems);
    localStorage.setItem('NODE_ORDER_UPDATE', JSON.stringify(schainNodeOrderItems));
    callback();
  }

}
